/* assets/rmap.js -- the zoomable Rova Map (map v2, 2026-10-02).
 *
 * Rob: "upgrade the page to a more modern edition so people can zoom into their area."
 * A real pan/pinch map from the whole globe down to a city, on a dark basemap we host
 * ourselves (Protomaps PMTiles on R2, read with Range requests: no tile provider, no key,
 * no per-view bill), drawn by MapLibre GL.
 *
 * WHAT THIS FILE IS ALLOWED TO DRAW (the server decides, lib/mapgrid.php explains):
 *   - regions + countries with a member count, inlined in the page (everyone);
 *   - k-anonymous hex bins, integers only, from GET /map/bins (members);
 *   - city-level beams of members who turned their beam on, from GET /map/beams (members).
 * Nothing here can resolve finer than those three payloads; zoom just draws them bigger.
 *
 * ENERGY RULES (phone heat, [[phone-heat-energy-evidence]]):
 *   - MapLibre only renders when something changes. There is NO animation loop: no auto
 *     rotation, no infinite pulse. The activity glow is a one-shot paint transition after load.
 *   - devicePixelRatio is capped at 2 (a dpr3 iPhone fills 2.25x the pixels for no visible gain
 *     on a basemap this soft).
 *   - The engine (MapLibre ~1.1 MB + pmtiles 19 KB) is NOT in app.js: this page only, as ES
 *     modules from /assets/vendor/ (versioned directories, so they cache like hashed files).
 *
 * GESTURES (the same contract the globe had, [[rova-map-globe]]): at rest the map is
 * COOPERATIVE: one finger scrolls the page straight through it and two fingers move the map;
 * on a desktop the wheel scrolls the page and Ctrl/Cmd+wheel zooms. Any deliberate use (a tap,
 * a button, a pinch, My area) hands the map one-finger pan + wheel zoom, and "Whole map" gives
 * the page back. Pinching the map never zooms the page: the map canvas never allows pinch-zoom.
 */
(function () {
  'use strict';
  var host = document.getElementById('rova-map2');
  if (!host) { return; }
  var D = {};
  try { D = JSON.parse(host.getAttribute('data-map') || '{}'); } catch (e) { D = {}; }
  var C = D.coarse || { r: [], c: [], k: 5 };
  var glEl = host.querySelector('.rmap2-gl');
  var wrap = host.parentNode;
  var card = wrap ? wrap.querySelector('.rmap2-card') : null;
  var live = document.getElementById('rmap2-status');
  var btnIn = host.querySelector('.rmap2-in'), btnOut = host.querySelector('.rmap2-out');
  var btnMe = host.querySelector('.rmap2-me'), btnBack = host.querySelector('.rmap2-back');
  var reduce = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  var ml = null, map = null, engaged = false, home = null;
  var bins = null, beams = null, beamById = {}, memberAsked = false;

  /* THE BASEMAP SOURCE, SPLIT IN TWO. The archive's directories (header, root, leaves, metadata: the
   * first D.dirsLen bytes) come from a small Cloudflare-cached copy on the media host; only tile bytes go
   * to the presigned R2 URL, which has no edge cache. Every map open reads the header and at least one
   * leaf directory before its first tile, so this takes two slow round trips off the critical path. The
   * header read is also started HERE, while the engine is still downloading, instead of after it boots. */
  function rng(u, off, len, signal) {
    return fetch(u, { signal: signal, headers: { Range: 'bytes=' + off + '-' + (off + len - 1) } })
      .then(function (r) { if (r.status !== 206 && r.status !== 200) { throw new Error('basemap ' + r.status); } return r.arrayBuffer(); })
      .then(function (b) { return { data: b.byteLength > len ? b.slice(0, len) : b }; });
  }
  var headP = (D.dirs && D.dirsLen && window.fetch) ? rng(D.dirs, 0, 16384).catch(function () { return null; }) : null;
  function SplitSource() {}
  /* THE SIGNED LINK EXPIRES (6-12 h after the page was rendered, R2::presignGet). A tab left open
   * overnight would otherwise go blank on the next pan. So: a refused tile read (403) asks
   * /map/tileurl for a fresh link ONCE and retries; and a tab that comes back into view more than
   * 5 hours after it got its link refreshes before anything is refused. The cache key stays constant,
   * so a new link never re-reads directories that are already in memory. */
  var tilesAt = Date.now(), refreshing = null;
  function freshTiles() {
    if (!refreshing) {
      refreshing = fetch('/map/tileurl', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { if (j && j.u) { D.tiles = j.u; tilesAt = Date.now(); } refreshing = null; return !!(j && j.u); })
        .catch(function () { refreshing = null; return false; });
    }
    return refreshing;
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && Date.now() - tilesAt > 5 * 3600 * 1000) { freshTiles(); }
  });
  var BASEKEY = 'rova-basemap-v1';
  SplitSource.prototype.getKey = function () { return BASEKEY; };
  SplitSource.prototype.getBytes = function (off, len, signal) {
    if (off === 0 && len === 16384 && headP) {
      var p = headP; headP = null;
      return p.then(function (h) { return (h && h.data.byteLength === len) ? h : rng(D.dirs, off, len, signal); });
    }
    if (D.dirs && off + len <= D.dirsLen) { return rng(D.dirs, off, len, signal); }
    var used = D.tiles;
    return rng(used, off, len, signal).catch(function (e) {
      var m = String(e && e.message);
      // R2 answers the odd 5xx under load: one quiet retry instead of a blank square on the map
      if (/ 5dd$/.test(m)) { return new Promise(function (r) { setTimeout(r, 400); }).then(function () { return rng(D.tiles, off, len, signal); }); }
      if (!/ 403$| 400$/.test(m)) { throw e; }
      // a read that failed on an OLD link just retries on the newer one; only a current link asks again
      if (D.tiles !== used) { return rng(D.tiles, off, len, signal); }
      return freshTiles().then(function (got) { if (!got) { throw e; } return rng(D.tiles, off, len, signal); });
    });
  };
  window.__rovaMapTiles = { age: function () { return Date.now() - tilesAt; }, refresh: freshTiles, url: function () { return D.tiles; } };

  function fail() { host.classList.add('rmap2-fail'); host.classList.remove('rmap2-ready'); }

  function theme() {
    var t = document.documentElement.getAttribute('data-theme');
    if (t === 'light' || t === 'dark') { return t; }
    return (window.matchMedia && matchMedia('(prefers-color-scheme: light)').matches) ? 'light' : 'dark';
  }

  /* ---------------------------------------------------------------- palette */
  var PAL = {
    dark: {
      bg: '#070b12', earth: '#121823', green: '#13201d', water: '#070b12', river: '#0c1320',
      road: '#232b38', roadHi: '#2c3646', border: '#3a4558', label: '#9aa4b5', labelDim: '#6b7586',
      halo: '#070b12', ocean: '#3d4a5e',
      data: '#f5c518', dataHi: '#ffe08a', dataInk: '#1a1306', ring: 'rgba(245,197,24,.55)',
      act: '#7dd3fc', hexLine: 'rgba(245,197,24,.75)', space: '#04070c', atmos: 'rgba(125,180,255,.35)'
    },
    light: {
      bg: '#cfe0f2', earth: '#f6f4ee', green: '#e3eedc', water: '#cfe0f2', river: '#bcd3ea',
      road: '#e4ddd0', roadHi: '#d9cfbd', border: '#9aa9bd', label: '#3d4656', labelDim: '#6b7586',
      halo: '#f6f4ee', ocean: '#5f7a99',
      data: '#2563eb', dataHi: '#1d4ed8', dataInk: '#ffffff', ring: 'rgba(37,99,235,.5)',
      act: '#7c3aed', hexLine: 'rgba(29,78,216,.8)', space: '#e8eef6', atmos: 'rgba(60,110,190,.25)'
    }
  };

  var NAME = ['coalesce', ['get', 'name:en'], ['get', 'name']];

  function baseLayers(p) {
    var src = 'base';
    return [
      { id: 'bg', type: 'background', paint: { 'background-color': p.bg } },
      { id: 'earth', type: 'fill', source: src, 'source-layer': 'earth', filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': p.earth } },
      { id: 'park', type: 'fill', source: src, 'source-layer': 'landuse', minzoom: 6,
        filter: ['in', 'kind', 'national_park', 'park', 'protected_area', 'nature_reserve', 'forest', 'wood'],
        paint: { 'fill-color': p.green, 'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0, 9, 0.9] } },
      { id: 'water', type: 'fill', source: src, 'source-layer': 'water', filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': p.water } },
      { id: 'river', type: 'line', source: src, 'source-layer': 'water', minzoom: 8, filter: ['in', 'kind', 'river'],
        paint: { 'line-color': p.river, 'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 8, 0.4, 11, 1.6] } },
      { id: 'road-minor', type: 'line', source: src, 'source-layer': 'roads', minzoom: 10,
        filter: ['all', ['==', 'kind', 'minor_road'], ['!has', 'is_tunnel']],
        paint: { 'line-color': p.road, 'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 10, 0.3, 11, 0.8] } },
      { id: 'road-major', type: 'line', source: src, 'source-layer': 'roads', minzoom: 6,
        filter: ['all', ['==', 'kind', 'major_road'], ['!has', 'is_tunnel']],
        paint: { 'line-color': p.road, 'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 6, 0.3, 11, 1.6] } },
      { id: 'road-hwy', type: 'line', source: src, 'source-layer': 'roads', minzoom: 4,
        filter: ['all', ['==', 'kind', 'highway'], ['!has', 'is_link'], ['!has', 'is_tunnel']],
        paint: { 'line-color': p.roadHi, 'line-width': ['interpolate', ['exponential', 1.6], ['zoom'], 4, 0.3, 8, 1, 11, 2.2] } },
      { id: 'border-country', type: 'line', source: src, 'source-layer': 'boundaries', filter: ['<=', 'kind_detail', 2],
        paint: { 'line-color': p.border, 'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.4, 6, 1] } },
      { id: 'border-region', type: 'line', source: src, 'source-layer': 'boundaries', minzoom: 3, filter: ['>', 'kind_detail', 2],
        paint: { 'line-color': p.border, 'line-width': 0.5, 'line-opacity': 0.55, 'line-dasharray': [2, 2] } },
      { id: 'lbl-ocean', type: 'symbol', source: src, 'source-layer': 'water', filter: ['in', 'kind', 'ocean', 'sea'],
        layout: { 'text-field': NAME, 'text-font': ['Noto Sans Regular'], 'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 6, 12],
                  'text-letter-spacing': 0.12, 'text-max-width': 8, 'text-transform': 'uppercase' },
        paint: { 'text-color': p.ocean, 'text-halo-color': p.halo, 'text-halo-width': 1 } },
      { id: 'lbl-country', type: 'symbol', source: src, 'source-layer': 'places', filter: ['==', 'kind', 'country'], maxzoom: 7,
        layout: { 'text-field': NAME, 'text-font': ['Noto Sans Medium'], 'text-transform': 'uppercase', 'text-letter-spacing': 0.06,
                  'text-size': ['interpolate', ['linear'], ['zoom'], 2, 9, 6, 14], 'text-max-width': 7,
                  'symbol-sort-key': ['coalesce', ['get', 'sort_key'], ['get', 'min_zoom']] },
        paint: { 'text-color': p.labelDim, 'text-halo-color': p.halo, 'text-halo-width': 1 } },
      { id: 'lbl-region', type: 'symbol', source: src, 'source-layer': 'places', filter: ['==', 'kind', 'region'], minzoom: 4, maxzoom: 9,
        layout: { 'text-field': ['step', ['zoom'], ['coalesce', ['get', 'ref:en'], ['get', 'ref'], NAME], 6, NAME],
                  'text-font': ['Noto Sans Regular'], 'text-transform': 'uppercase', 'text-letter-spacing': 0.08,
                  'text-size': ['interpolate', ['linear'], ['zoom'], 4, 9, 8, 13], 'symbol-sort-key': ['get', 'sort_key'] },
        paint: { 'text-color': p.labelDim, 'text-halo-color': p.halo, 'text-halo-width': 1, 'text-opacity': 0.8 } },
      { id: 'lbl-city', type: 'symbol', source: src, 'source-layer': 'places', filter: ['==', 'kind', 'locality'], minzoom: 3,
        layout: { 'text-field': NAME, 'text-font': ['case', ['<=', ['get', 'min_zoom'], 5], ['literal', ['Noto Sans Medium']], ['literal', ['Noto Sans Regular']]],
                  'text-size': ['interpolate', ['linear'], ['zoom'], 3, 10, 8, 13, 11, 15], 'text-max-width': 8,
                  'text-padding': 6, 'symbol-sort-key': ['coalesce', ['get', 'sort_key'], ['get', 'min_zoom']] },
        paint: { 'text-color': p.label, 'text-halo-color': p.halo, 'text-halo-width': 1.2 } }
    ];
  }

  /* ---------------------------------------------------------------- data -> GeoJSON */
  function fc(features) { return { type: 'FeatureCollection', features: features }; }
  function pt(lng, lat, props) { return { type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: props }; }

  function coarseFC() {
    var f = [], i, r;
    for (i = 0; i < (C.r || []).length; i++) {
      r = C.r[i];
      f.push(pt(r[0], r[1], { kind: 'r', n: r[2], name: r[3], act: r[4] | 0, t: r[3] + '\n' + Number(r[2]).toLocaleString() }));
    }
    for (i = 0; i < (C.c || []).length; i++) {
      r = C.c[i];
      f.push(pt(r[1], r[2], { kind: 'c', cc: r[0], n: r[3], name: r[4], act: r[5] | 0, t: r[4] + '\n' + Number(r[3]).toLocaleString() }));
    }
    return fc(f);
  }

  /* Hex geometry: the exact inverse of MapGrid::hexOf (lib/mapgrid.php). */
  function unmerc(x, y) {
    var lng = x * 360 - 180;
    var lat = Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI;
    return [lng, lat];
  }
  function hexRing(lv, q, r, px) {
    var w = 512 * Math.pow(2, lv), s = px, X = s * (Math.sqrt(3) * q + Math.sqrt(3) / 2 * r), Y = s * 1.5 * r, ring = [], a, k;
    for (k = 0; k < 6; k++) {
      a = Math.PI / 180 * (60 * k - 30);
      ring.push(unmerc((X + s * Math.cos(a)) / w, (Y + s * Math.sin(a)) / w));
    }
    ring.push(ring[0]);
    return { ring: ring, c: unmerc(X / w, Y / w) };
  }
  function binsFC(points) {
    var f = [], b, h, i, px = (bins && bins.px) || 34;
    for (i = 0; bins && i < bins.bins.length; i++) {
      b = bins.bins[i];
      h = hexRing(b[0], b[1], b[2], px);
      var props = { lv: b[0], n: b[3], act: b[4], t: b[3] + '+' };
      f.push(points ? pt(h.c[0], h.c[1], props) : { type: 'Feature', geometry: { type: 'Polygon', coordinates: [h.ring] }, properties: props });
    }
    return fc(f);
  }
  function beamsFC() {
    var f = [], b, i;
    for (i = 0; beams && i < beams.length; i++) {
      b = beams[i];
      f.push(pt(b[4], b[5], { id: b[0], av: 'av-' + b[0], me: (b[6] & 4) ? 1 : 0, on: (b[6] & 1) ? 1 : 0 }));
    }
    return fc(f);
  }

  /* ---------------------------------------------------------------- the real sun
   * Rob 2026-10-02: "its daytime why is the sun on the opposite side of the earth?" MapLibre's default
   * light is anchored to the VIEWPORT at a fixed angle, so the atmosphere glow sat on the far limb whatever
   * the time. Now: the subsolar point from the clock (low-precision solar position, error well under 0.1
   * degree), the atmosphere's light anchored to the MAP pointing at it, and a soft night shade over the
   * dark hemisphere. Recomputed every 5 minutes by one timer (no animation loop), skipped while hidden. */
  var R2D = 180 / Math.PI, D2R = Math.PI / 180;
  function subsolar(ms) {
    var n = ms / 86400000 + 2440587.5 - 2451545.0;                    // days since J2000.0
    var L = (280.460 + 0.9856474 * n) % 360, g = ((357.528 + 0.9856003 * n) % 360) * D2R;
    var lam = (L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * D2R;  // ecliptic longitude
    var eps = (23.439 - 0.0000004 * n) * D2R;
    var dec = Math.asin(Math.sin(eps) * Math.sin(lam));
    var ra = Math.atan2(Math.cos(eps) * Math.sin(lam), Math.cos(lam)) * R2D;
    var gmst = (280.46061837 + 360.98564736629 * n) % 360;
    var lng = ((ra - gmst) % 360 + 540) % 360 - 180;
    return { lat: dec * R2D, lng: lng };
  }
  /* MapLibre's globe space: (lng, lat) -> [sin(lng)cos(lat), sin(lat), cos(lng)cos(lat)]; a map-anchored
   * light at [r, az, polar] is -[cos(az+90)sin(p), sin(az+90)sin(p), cos(p)] in that space (getSunPos). */
  function sunLight(sun) {
    var la = sun.lat * D2R, lo = sun.lng * D2R;
    var sx = Math.sin(lo) * Math.cos(la), sy = Math.sin(la), sz = Math.cos(lo) * Math.cos(la);
    var polar = Math.acos(Math.max(-1, Math.min(1, -sz))) * R2D;
    var az = Math.atan2(-sy, -sx) * R2D - 90;
    return { anchor: 'map', position: [1.5, az, polar], color: '#ffffff', intensity: 0.5 };
  }
  /* The night hemisphere as one polygon: the terminator latitude for every meridian, closed over the dark
   * pole. Equinox (declination ~0) is clamped so the tangent never divides by zero. */
  function nightFC(sun) {
    var dec = sun.lat; if (Math.abs(dec) < 0.05) { dec = dec < 0 ? -0.05 : 0.05; }
    var td = Math.tan(dec * D2R), ring = [], lng, lat, pole = dec > 0 ? -85 : 85;
    for (lng = -180; lng <= 180; lng += 3) {
      lat = Math.atan(-Math.cos((lng - sun.lng) * D2R) / td) * R2D;
      ring.push([lng, Math.max(-85, Math.min(85, lat))]);
    }
    var edge = ring.slice();                                         // the terminator alone, for the soft edge
    ring.push([180, pole], [-180, pole], ring[0]);
    return fc([{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} },
               { type: 'Feature', geometry: { type: 'LineString', coordinates: edge }, properties: {} }]);
  }
  function sunNow() { return subsolar(Date.now()); }
  var sunTimer = null;
  function sunTick() {
    if (!map || document.visibilityState === 'hidden') { return; }
    var sun = sunNow(), src = map.getSource('night');
    if (src) { src.setData(nightFC(sun)); }
    try { map.setLight(sunLight(sun), { validate: false }); } catch (e) { /* engine without setLight */ }
  }
  window.__rovaMapSun = { subsolar: subsolar, now: sunNow };

  /* ---------------------------------------------------------------- style */
  function lvRange(lv) {
    var L = (bins && bins.lv) || [4, 5, 6, 7, 8];
    var first = L[0], last = L[L.length - 1];
    return [lv === first ? 0 : lv, lv === last ? 24 : lv + 1];
  }

  function style(t) {
    var p = PAL[t] || PAL.dark;
    var layers = baseLayers(p);
    var member = !!D.member;
    // the night shade: under labels and data, faded out by zoom 4.5 so a zoomed-in city is never dimmed
    var labelAt = 0, li;
    for (li = 0; li < layers.length; li++) { if (layers[li].type === 'symbol') { labelAt = li; break; } }
    var nightInk = t === 'light' ? '#0b1e3a' : '#000000', nightA = t === 'light' ? 0.2 : 0.42;
    layers.splice(labelAt || layers.length, 0,
      { id: 'night', type: 'fill', source: 'night', maxzoom: 5, filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': nightInk, 'fill-antialias': false,
                 'fill-opacity': ['interpolate', ['linear'], ['zoom'], 0, nightA, 3, nightA * 0.8, 4.5, 0] } },
      // twilight: a wide blurred line on the terminator softens the edge into a gradient
      { id: 'night-edge', type: 'line', source: 'night', maxzoom: 5, filter: ['==', ['geometry-type'], 'LineString'],
        paint: { 'line-color': nightInk, 'line-width': ['interpolate', ['linear'], ['zoom'], 0, 14, 4, 40],
                 'line-blur': ['interpolate', ['linear'], ['zoom'], 0, 14, 4, 40],
                 'line-opacity': ['interpolate', ['linear'], ['zoom'], 0, nightA * 0.6, 3, nightA * 0.5, 4.5, 0] } });
    // regions + countries: a soft halo sized by members, a core dot, a one-shot activity ring
    layers.push(
      { id: 'c-halo', type: 'circle', source: 'coarse',
        paint: { 'circle-color': p.data, 'circle-opacity': member ? ['interpolate', ['linear'], ['zoom'], 3.5, 0.16, 6, 0.06] : 0.16,
                 'circle-blur': 0.5,
                 'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, ['+', 5, ['*', 0.32, ['sqrt', ['get', 'n']]]], 6, ['+', 12, ['*', 0.6, ['sqrt', ['get', 'n']]]]] } },
      { id: 'c-act', type: 'circle', source: 'coarse', filter: ['>', ['get', 'act'], 0],
        paint: { 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': p.act,
                 'circle-stroke-width': ['interpolate', ['linear'], ['get', 'act'], 1, 1, 3, 2.2],
                 'circle-stroke-opacity': 0.0, 'circle-radius': 4 } },
      { id: 'c-dot', type: 'circle', source: 'coarse',
        paint: { 'circle-color': p.data, 'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 2.6, 6, 4.5],
                 'circle-stroke-color': p.halo, 'circle-stroke-width': 1 } },
      { id: 'c-lbl', type: 'symbol', source: 'coarse', minzoom: 1.6,
        layout: { 'text-field': ['get', 't'], 'text-font': ['Noto Sans Medium'], 'text-size': 11, 'text-offset': [0, 1.1],
                  'text-anchor': 'top', 'text-max-width': 9, 'symbol-sort-key': ['-', 0, ['get', 'n']], 'text-padding': 4 },
        paint: { 'text-color': p.dataHi, 'text-halo-color': p.halo, 'text-halo-width': 1.4 } }
    );
    if (member) {
      var L = (bins && bins.lv) || [4, 5, 6, 7, 8], i, lv, rg;
      for (i = 0; i < L.length; i++) {
        lv = L[i]; rg = lvRange(lv);
        layers.push(
          { id: 'b-fill-' + lv, type: 'fill', source: 'bins', minzoom: rg[0], maxzoom: rg[1], filter: ['==', ['get', 'lv'], lv],
            paint: { 'fill-color': p.data, 'fill-opacity': ['interpolate', ['linear'], ['get', 'n'], 5, 0.16, 50, 0.32, 500, 0.5] } },
          { id: 'b-line-' + lv, type: 'line', source: 'bins', minzoom: rg[0], maxzoom: rg[1], filter: ['==', ['get', 'lv'], lv],
            paint: { 'line-color': p.hexLine, 'line-width': ['interpolate', ['linear'], ['get', 'act'], 0, 0.8, 3, 2.2],
                     'line-opacity': ['interpolate', ['linear'], ['get', 'act'], 0, 0.55, 3, 1] } },
          { id: 'b-lbl-' + lv, type: 'symbol', source: 'binpts', minzoom: rg[0], maxzoom: rg[1], filter: ['==', ['get', 'lv'], lv],
            layout: { 'text-field': ['get', 't'], 'text-font': ['Noto Sans Medium'], 'text-size': 12, 'text-allow-overlap': false },
            paint: { 'text-color': p.dataHi, 'text-halo-color': p.halo, 'text-halo-width': 1.4 } }
        );
      }
      layers.push(
        { id: 'k-cluster', type: 'circle', source: 'beams', minzoom: 4, filter: ['has', 'point_count'],
          paint: { 'circle-color': p.halo, 'circle-stroke-color': p.data, 'circle-stroke-width': 2,
                   'circle-radius': ['interpolate', ['linear'], ['get', 'point_count'], 2, 13, 50, 22] } },
        { id: 'k-cluster-n', type: 'symbol', source: 'beams', minzoom: 4, filter: ['has', 'point_count'],
          layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Medium'], 'text-size': 12, 'text-allow-overlap': true },
          paint: { 'text-color': p.dataHi } },
        /* Rob 2026-10-03 (light theme, zoomed to Albuquerque..Memphis): "Circles still don't have profile
         * pictures." k-av started at z6.5 while k-dot started at z4, so between z4 and z6.5 (the whole
         * multi-state view) every person was an EMPTY ring, in both themes. The face now starts with the
         * ring, the ring is a little larger at low zoom so the face reads, and a beam with no photo gets
         * the gold initial: never an empty ring. */
        { id: 'k-dot', type: 'circle', source: 'beams', minzoom: 4, filter: ['!', ['has', 'point_count']],
          paint: { 'circle-color': p.halo, 'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 9, 6.5, 12, 9, 15],
                   'circle-stroke-color': ['case', ['==', ['get', 'me'], 1], p.dataHi, p.data],
                   'circle-stroke-width': ['case', ['==', ['get', 'me'], 1], 3, 2] } },
        { id: 'k-av', type: 'symbol', source: 'beams', minzoom: 4, filter: ['!', ['has', 'point_count']],
          layout: { 'icon-image': ['get', 'av'], 'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 6.5, 0.66, 9, 0.82],
                    'icon-allow-overlap': true, 'icon-ignore-placement': true } }
      );
    }
    var s = {
      version: 8,
      glyphs: D.glyphs,
      sources: {
        base: { type: 'vector', url: 'pmtiles://' + (D.dirs ? BASEKEY : D.tiles), attribution: '' },
        coarse: { type: 'geojson', data: coarseFC() },
        night: { type: 'geojson', data: nightFC(sunNow()), maxzoom: 4 }
      },
      light: sunLight(sunNow()),
      // reduced motion: no transitions anywhere in the style (the sun moving every 5 minutes included)
      transition: reduce ? { duration: 0, delay: 0 } : { duration: 300, delay: 0 },
      layers: layers,
      projection: { type: 'globe' },
      /* With the light now facing the sun, a full-strength atmosphere hazes the WHOLE daylit disc (pale blue
       * in dark theme, white in light): 0.3 / 0.15 keeps the sunward glow and lets the land read through. */
      sky: { 'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, t === 'light' ? 0.15 : 0.3, 5, t === 'light' ? 0.15 : 0.3, 7, 0] }
    };
    if (member) {
      s.sources.bins = { type: 'geojson', data: binsFC(false) };
      s.sources.binpts = { type: 'geojson', data: binsFC(true) };
      s.sources.beams = { type: 'geojson', data: beamsFC(), cluster: true, clusterRadius: 42, clusterMaxZoom: 8 };
    }
    return s;
  }

  /* ---------------------------------------------------------------- the card + status */
  var ACT = ['quiet in the last 24 hours', 'some activity in the last 24 hours', 'busy in the last 24 hours', 'very busy in the last 24 hours'];
  /* THE MONOGRAM LETTER: the first CODE POINT, uppercased -- a mirror of NameText::initial() (lib/nametext.php) and app.js
     nameInitial(). charAt(0) / s[0] cut an emoji's UTF-16 surrogate pair in half ("💖heartless" -> a lone \uD83D, drawn
     as the replacement box: the iOS media-ring bug, 2026-10-17). Strips controls / zero-width / bidi marks like
     NameText::plain(); re-cuts after toUpperCase() (sharp s -> SS). Empty -> d (default '?'). */
  var FCP_CTRL = /[\u0000-\u001F\u007F-\u009F]+/g, FCP_ZERO = /[\u00AD\u200B-\u200F\u2028\u2029\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF]+/g;
  var FCP_ONE = /^(?:[\uD800-\uDBFF][\uDC00-\uDFFF]|[\s\S])/;
  function firstCp(s, d) {
    var m = FCP_ONE.exec(String(s == null ? '' : s).replace(FCP_CTRL, ' ').replace(FCP_ZERO, '').trim());
    if (!m) { return d == null ? '?' : d; }
    var u = m[0].toUpperCase();
    return (FCP_ONE.exec(u) || [u])[0];
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) { return c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'; });
  }
  function say(t) { if (live) { live.textContent = t; } }
  function showCard(html) {
    if (!card) { return; }
    card.innerHTML = '<button type="button" class="rmap2-x xmark" aria-label="Close"></button>' + html;
    card.hidden = false;
    var x = card.querySelector('.rmap2-x');
    if (x) { x.addEventListener('click', hideCard); }
  }
  function hideCard() { if (card) { card.hidden = true; card.textContent = ''; } }

  function cardFor(f) {
    var pr = f.properties || {}, lid = f.layer && f.layer.id || '';
    if (lid.indexOf('c-') === 0) {
      var region = pr.kind === 'r';
      showCard('<h4>' + esc(pr.name) + '</h4><p class="rmap2-n">' + Number(pr.n).toLocaleString() + ' members</p>'
        + '<p class="rmap2-fine">' + (region
          ? 'Members here share only this region, worked out from their device clock, so they are counted here and never placed more precisely.'
          : 'Members seen from this country. Country is the finest level the map shows outside the US.')
        + ' This area is ' + ACT[pr.act | 0] + '.</p>');
      say(pr.name + ', ' + pr.n + ' members.');
      return true;
    }
    if (lid.indexOf('b-') === 0) {
      showCard('<h4>' + esc(pr.n + '+ members') + '</h4>'
        + '<p class="rmap2-fine">Members beaming from a city inside this area. Areas need at least ' + (C.k || 5)
        + ' people to appear and the number is rounded down. This area is ' + ACT[pr.act | 0] + '.</p>');
      say('An area with ' + pr.n + ' or more members.');
      return true;
    }
    if (lid === 'k-dot' || lid === 'k-av') {
      var b = beamById[pr.id];
      if (!b) { return false; }
      var me = (b[6] & 4) !== 0, prof = '/@' + encodeURIComponent(b[1]);
      /* WHO = ONE LINK (2026-10-03, Rob: "Tapping the name picture or username here should take me to
       * their profile also"). Avatar + name + handle sit in a single <a> to the plain /@handle (no query:
       * the app's MapScreen WebView hands exactly that shape to the native Profile screen). The close X
       * is prepended by showCard OUTSIDE this link, and "View profile" stays its own sibling anchor, so
       * nothing nests. The card is a sibling of the map host, so MapLibre's click never sees these taps. */
      showCard('<a class="rmap2-who" href="' + prof + '">'
        + (b[3] ? '<img class="rmap2-av" alt="" src="' + esc(b[3]) + '">' : '')
        + '<h4>' + esc(b[2]) + (me ? ' <span class="muted">(you)</span>' : '') + '</h4>'
        + '<p class="rmap2-n">@' + esc(b[1]) + ((b[6] & 1) ? ' <span class="cp-chip green">online</span>' : '') + '</p>'
        + '</a>'
        + '<p class="rmap2-fine">' + ((b[6] & 2) ? 'Traveling. ' : '') + 'Shown at the middle of their city plus a small fixed offset, never their address.</p>'
        + '<a class="btn sm" href="' + prof + '">View profile</a>');
      say(b[2] + ', beaming.');
      return true;
    }
    return false;
  }

  /* ---------------------------------------------------------------- engagement */
  function engage() {
    if (engaged || !map) { return; }
    engaged = true;
    try { map.cooperativeGestures.disable(); } catch (e) { /* older engine: nothing to hand over */ }
    host.classList.add('rmap2-on');
    if (btnBack) { btnBack.hidden = false; }
  }
  function release() {
    engaged = false;
    try { map.cooperativeGestures.enable(); } catch (e) {}
    host.classList.remove('rmap2-on');
    if (btnBack) { btnBack.hidden = true; }
  }
  function go(cam) {
    if (!map) { return; }
    if (cam.center && (cam.zoom || 0) >= 4) { avWarmNear(cam.center, cam.zoom); }
    if (reduce) { map.jumpTo(cam); } else { map.flyTo(Object.assign({ duration: 1400, essential: false }, cam)); }
  }
  function homeView() {
    var w = glEl.clientWidth || 360, h = glEl.clientHeight || 360;
    var z = Math.log(Math.min(w, h) * 0.9 * Math.PI / 512) / Math.LN2;
    var lng = D.me ? D.me[0] : -40;
    return { center: [lng, 22], zoom: Math.max(0, Math.min(2, z)) };
  }
  function syncZoomBtns() {
    if (!map) { return; }
    var z = map.getZoom();
    if (btnIn) { btnIn.disabled = z >= map.getMaxZoom() - 0.01; }
    if (btnOut) { btnOut.disabled = z <= map.getMinZoom() + 0.01; }
  }

  /* ---------------------------------------------------------------- members-only layers */
  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function loadMember(force) {
    if (!D.member || !map || (memberAsked && !force)) { return; }
    memberAsked = true;
    getJSON('/map/bins').then(function (j) {
      if (!j || !j.ok || !map) { return; }
      bins = j;
      var s1 = map.getSource('bins'), s2 = map.getSource('binpts');
      if (s1) { s1.setData(binsFC(false)); }
      if (s2) { s2.setData(binsFC(true)); }
    });
    loadBeams();
  }
  function loadBeams() {
    getJSON('/map/beams').then(function (j) {
      if (!j || !j.ok || !map) { return; }
      beams = j.beams || [];
      beamById = {};
      for (var i = 0; i < beams.length; i++) { beamById[beams[i][0]] = beams[i]; }
      var s = map.getSource('beams');
      if (s) { s.setData(beamsFC()); }
      // only where someone is plausibly about to look: here if already zoomed in, else their own area
      var c = map.getCenter();
      if (map.getZoom() >= 4) { avWarmNear([c.lng, c.lat], map.getZoom()); }
      else if (D.me && D.me[4] !== 'country' && D.me[4] !== 'region') { avWarmNear([D.me[0], D.me[1]], D.me[2], 12); }
    });
  }

  /* BEAM AVATARS (rebuilt 2026-10-02, Rob: "map takes too long to load peoples avatars").
   * BEFORE: an avatar was requested only from styleimagemissing, which MapLibre fires after the camera
   * stops and the symbols lay out, so every fetch started at the END of the flight (0.5-1.4 s after the
   * beams had arrived) and the marker sat as a transparent hole meanwhile. Measured: beams -> all painted
   * 1.2 s at 1440, 2.2-2.5 s on a 390 slow-4G phone.
   * NOW: the moment beams arrive, and the moment a flight is aimed somewhere, the avatars nearest that
   * point are fetched (8 at a time, nearest first) and decoded OFF the main thread (createImageBitmap)
   * into a ready pixel cache; when the symbol asks, the real pixels are already there. Anything not yet
   * cached gets an initial on gold, never a hole. URLs are whatever the server chose (the 128 px _av
   * variant, the blurred _nb for a gated viewer, a poster still for a video avatar): this file never
   * picks or alters an avatar. */
  var AV = 96, AVPR = 3;                      // 96 px at pixelRatio 3 = a 32 css px icon, sharp at DPR 3
  var avPix = {}, avState = {}, avQ = [], avBusy = 0, AVMAX = 8;
  window.__rovaMapAvReal = {};
  function avCircle(draw) {
    var cv = document.createElement('canvas'); cv.width = AV; cv.height = AV;
    var g = cv.getContext('2d');
    g.save(); g.beginPath(); g.arc(AV / 2, AV / 2, AV / 2 - 3, 0, Math.PI * 2); g.closePath(); g.clip();
    draw(g);
    g.restore();
    return g.getImageData(0, 0, AV, AV);
  }
  function avPlaceholder(b) {
    return avCircle(function (g) {
      var gr = g.createLinearGradient(0, 0, AV, AV);
      gr.addColorStop(0, '#a9760a'); gr.addColorStop(0.45, '#f5c518'); gr.addColorStop(1, '#ffe89a');
      g.fillStyle = gr; g.fillRect(0, 0, AV, AV);
      var ch = firstCp(b && (b[2] || b[1]), 'R');
      g.fillStyle = '#1a1306'; g.font = '700 ' + Math.round(AV * 0.46) + 'px system-ui,-apple-system,Segoe UI,sans-serif';
      g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(ch, AV / 2, AV / 2 + AV * 0.03);
    });
  }
  function avPaint(id) {
    var key = 'av-' + id, px = avPix[id];
    if (!map || !px) { return; }
    if (map.hasImage(key)) { map.updateImage(key, px); window.__rovaMapAvReal[key] = 1; }
  }
  function avPump() {
    while (avBusy < AVMAX && avQ.length) {
      var id = avQ.shift(), b = beamById[id];
      if (!b || !b[3] || avState[id]) { continue; }
      avState[id] = 1; avBusy++;
      (function (id, url) {
        fetch(url, { mode: 'cors', credentials: 'omit' })
          .then(function (r) { if (!r.ok) { throw 0; } return r.blob(); })
          .then(function (bl) { return createImageBitmap(bl); })              // decode off the main thread
          .then(function (bm) {
            avPix[id] = avCircle(function (g) {
              var sc = Math.max(AV / bm.width, AV / bm.height);
              g.drawImage(bm, (AV - bm.width * sc) / 2, (AV - bm.height * sc) / 2, bm.width * sc, bm.height * sc);
            });
            if (bm.close) { bm.close(); }
            avState[id] = 2;
            avPaint(id);
            (avWaiters[id] || []).forEach(function (w) { w(); }); delete avWaiters[id];
          })
          .catch(function () { avState[id] = 3; (avWaiters[id] || []).forEach(function (w) { w(); }); delete avWaiters[id]; })   // placeholder stays
          .then(function () { avBusy--; avPump(); });
      })(id, b[3]);
    }
  }
  function avWant(ids, front) {
    var fresh = ids.filter(function (id) { return !avState[id] && avQ.indexOf(id) < 0; });
    avQ = front ? fresh.concat(avQ) : avQ.concat(fresh);
    avPump();
  }
  /* The beams nearest a point, nearest first: those are the markers about to be on screen. The radius
   * follows the zoom (a 1440 px wide view at z9 spans ~1.5 degrees of longitude), capped at 40 avatars. */
  function avWarmNear(c, z, cap) {
    if (!beams || !beams.length) { return; }
    // about one viewport around the point: a 512 px tile spans 360/2^z degrees, the box is ~1-3 tiles wide
    var span = 360 / Math.pow(2, Math.max(4, z || 8)) * Math.max(1, (glEl.clientWidth || 400) / 512) * 0.75;
    var near = [], i, b, dx, dy;
    for (i = 0; i < beams.length; i++) {
      b = beams[i]; if (!b[3]) { continue; }
      dx = (b[4] - c[0]) * Math.cos(c[1] * D2R); dy = b[5] - c[1];
      if (Math.abs(dx) <= span && Math.abs(dy) <= span) { near.push([dx * dx + dy * dy, b[0]]); }
    }
    near.sort(function (a, b2) { return a[0] - b2[0]; });
    // a slow or metered line warms fewer: the ones nearest the centre are the ones on screen first
    var cn = navigator.connection || {}, slow = cn.saveData || /(^|-)2g|3g/.test(cn.effectiveType || '');
    avWant(near.slice(0, Math.min(cap || 12, slow ? 6 : 12)).map(function (x) { return x[1]; }), true);
  }
  /* MapLibre 6 IGNORES an image added from a 'styleimagemissing' listener for the layout that asked
   * ("Image av-N could not be loaded ... use map.setMissingStyleImageResolver()"): the symbol was dropped
   * and only appeared after some LATER re-layout (another zoom or pan). That, more than the network, is
   * why avatars "took too long" (Rob 2026-10-02). The resolver may return a promise and the layout waits
   * for it: real pixels if they are cached or land within AVWAIT ms, else the gold initial, swapped for the
   * real face by updateImage the moment it arrives. */
  var AVWAIT = 300, avWaiters = {};
  function avResolve(id) {
    var key = 'av-' + id, b = beamById[id];
    if (!b || map.hasImage(key)) { return; }
    if (avPix[id] || !b[3]) { avatarImage(id); return; }   // a cached face, or no photo at all (the gold initial)
    avWant([id], true);
    return new Promise(function (res) {
      var done = false, fin = function () { if (done) { return; } done = true; avatarImage(id); res(); };
      (avWaiters[id] = avWaiters[id] || []).push(fin);
      setTimeout(fin, avState[id] === 3 ? 0 : AVWAIT);
    });
  }
  function avatarImage(id) {
    var b = beamById[id], key = 'av-' + id;
    if (!b || map.hasImage(key)) { return; }
    var px = avPix[id];
    if (px) { window.__rovaMapAvReal[key] = 1; }
    try { map.addImage(key, px || avPlaceholder(b), { pixelRatio: AVPR }); } catch (e) { return; }
    if (!px && b[3]) { avWant([id], true); }
  }

  /* ---------------------------------------------------------------- boot */
  function boot(mods) {
    ml = mods[0];
    var pm = mods[1];
    var proto = new pm.Protocol({ metadata: false });
    if (D.dirs) { proto.add(new pm.PMTiles(new SplitSource())); }
    ml.addProtocol('pmtiles', proto.tile);
    var hv = homeView();
    home = hv;
    try {
      map = new ml.Map({
        container: glEl,
        style: style(theme()),
        center: hv.center, zoom: hv.zoom,
        minZoom: 0, maxZoom: 11,
        maxPitch: 0, pitchWithRotate: false, dragRotate: false, touchPitch: false,
        renderWorldCopies: false,
        attributionControl: false,
        cooperativeGestures: true,
        fadeDuration: reduce ? 0 : 150,
        localIdeographFontFamily: 'sans-serif',
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
        canvasContextAttributes: { antialias: false, powerPreference: 'low-power' },
        validateStyle: false
      });
    } catch (e) { fail(); return; }
    // read-only handle for the verification rigs (same pattern as window.__rovaGlobe)
    window.__rovaMap = {
      map: map,
      state: function () {
        return { zoom: map.getZoom(), center: map.getCenter().toArray(), engaged: engaged, moving: map.isMoving(),
                 bins: bins ? bins.bins.length : -1, beams: beams ? beams.length : -1, coop: !!(map.cooperativeGestures && map.cooperativeGestures.isEnabled()) };
      }
    };
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation && map.keyboard.disableRotation();
    var canvas = map.getCanvas();
    canvas.setAttribute('aria-label', host.getAttribute('data-label') || 'Map');
    canvas.setAttribute('role', 'img');

    map.on('error', function (ev) {
      if (ev && ev.error && /webgl/i.test(String(ev.error.message || ev.error))) { fail(); }
    });
    if (map.setMissingStyleImageResolver) {
      map.setMissingStyleImageResolver(function (id) { return /^av-[0-9]+$/.test(id) ? avResolve(+id.slice(3)) : undefined; });
    }
    map.on('styleimagemissing', function (ev) {
      var id = String(ev.id || '');
      if (id.indexOf('av-') === 0) { avatarImage(+id.slice(3)); }
    });
    map.once('load', function () {
      host.classList.add('rmap2-ready');
      if (!sunTimer) {
        sunTimer = setInterval(sunTick, 5 * 60 * 1000);
        document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') { sunTick(); } });
      }
      syncZoomBtns();
      pulseOnce();
      if (D.member) {
        var ric = window.requestIdleCallback || function (fn) { return setTimeout(fn, 600); };
        ric(function () { loadMember(); }, { timeout: 1500 });
      }
    });
    map.on('zoomend', syncZoomBtns);
    // a pinch or wheel into a city warms that city's avatars too (a button flight already did, in go())
    /* ...and so does ANY camera path once it is below zoom 5 (a pinch, a wheel, a flight that did not come
     * through go()): the centre is then already close to where it will land, so fetching starts during the
     * descent instead of after the symbols lay out. Throttled to 4 a second; the loop is ~100 beams. */
    var avMoveAt = 0;
    map.on('move', function () {
      var now = Date.now();
      if (!beams || now - avMoveAt < 250 || map.getZoom() < 4) { return; }
      avMoveAt = now; var c = map.getCenter(); avWarmNear([c.lng, c.lat], map.getZoom());
    });
    map.on('moveend', function () { if (beams && map.getZoom() >= 4) { var c = map.getCenter(); avWarmNear([c.lng, c.lat], map.getZoom()); } });
    map.on('zoom', function () { if (D.member && !memberAsked && map.getZoom() >= 3.2) { loadMember(); } });
    // deliberate use hands the map one-finger pan + wheel zoom (see the header)
    map.on('click', function (ev) {
      engage();
      var ids = ['k-av', 'k-dot', 'k-cluster'];
      if (D.member && bins) { for (var i = 0; i < bins.lv.length; i++) { ids.push('b-fill-' + bins.lv[i]); } }
      ids.push('c-dot', 'c-halo');
      var hit = map.queryRenderedFeatures(ev.point, { layers: ids.filter(function (l) { return !!map.getLayer(l); }) });
      if (!hit.length) { hideCard(); return; }
      var f = hit[0];
      if (f.layer.id === 'k-cluster') {
        var src = map.getSource('beams');
        src.getClusterExpansionZoom(f.properties.cluster_id).then(function (z) {
          go({ center: f.geometry.coordinates, zoom: Math.min(z + 0.2, 11) });
        }).catch(function () {});
        return;
      }
      if (!cardFor(f)) { hideCard(); }
    });
    map.on('dblclick', engage);
    map.on('touchstart', function (ev) { if (ev.originalEvent && ev.originalEvent.touches && ev.originalEvent.touches.length > 1) { engage(); } });
    map.on('wheel', function (ev) { var o = ev.originalEvent; if (o && (o.ctrlKey || o.metaKey)) { engage(); } });
    ['k-dot', 'k-cluster', 'c-dot'].forEach(function (l) {
      map.on('mouseenter', l, function () { canvas.style.cursor = 'pointer'; });
      map.on('mouseleave', l, function () { canvas.style.cursor = ''; });
    });

    if (btnIn) { btnIn.addEventListener('click', function () { engage(); map.zoomIn({ duration: reduce ? 0 : 300 }); if (D.member) { loadMember(); } }); }
    if (btnOut) { btnOut.addEventListener('click', function () { engage(); map.zoomOut({ duration: reduce ? 0 : 300 }); }); }
    if (btnMe && D.me) {
      btnMe.addEventListener('click', function () {
        engage();
        if (D.member) { loadMember(); }
        go({ center: [D.me[0], D.me[1]], zoom: D.me[2] });
        say('Showing ' + D.me[3] + '.');
      });
    }
    if (btnBack) {
      btnBack.addEventListener('click', function () {
        hideCard();
        go({ center: home.center, zoom: home.zoom });
        release();
        say('Showing the whole map.');
      });
    }
    // the accessible country list under the map points the camera
    var list = document.getElementById('rglobe-list');
    if (list) {
      list.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('button[data-cc]') : null;
        if (!b) { return; }
        var cc = b.getAttribute('data-cc'), i;
        engage();
        if (cc === 'US') { go({ center: [-97, 39], zoom: 3 }); say('United States.'); return; }
        for (i = 0; i < (C.c || []).length; i++) {
          if (C.c[i][0] === cc) { go({ center: [C.c[i][1], C.c[i][2]], zoom: 4.2 }); say(C.c[i][4] + '.'); return; }
        }
      });
    }
    // the beam switch (app.js) changes who is on the map: refresh the personal layer once it settles
    var beamBtn = document.getElementById('rmap-beam');
    if (beamBtn && D.member) {
      beamBtn.addEventListener('click', function () { if (memberAsked) { setTimeout(loadBeams, 1500); } });
    }
    // theme flips (the toggle writes data-theme on <html>) rebuild the style in place
    if (window.MutationObserver) {
      var last = theme();
      new MutationObserver(function () {
        var t = theme();
        if (t !== last && map) { last = t; map.setStyle(style(t), { diff: true }); }
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }
  }

  /* The activity rings: one expanding pulse after load, then still. A paint TRANSITION, so
   * MapLibre renders for its duration and stops; there is no loop to keep a phone awake. */
  function pulseOnce() {
    if (!map || !map.getLayer('c-act')) { return; }
    if (reduce) {
      map.setPaintProperty('c-act', 'circle-stroke-opacity', 0.7);
      map.setPaintProperty('c-act', 'circle-radius', 9);
      return;
    }
    map.setPaintProperty('c-act', 'circle-stroke-opacity-transition', { duration: 0 });
    map.setPaintProperty('c-act', 'circle-radius-transition', { duration: 1800 });
    map.setPaintProperty('c-act', 'circle-stroke-opacity', 0.9);
    map.setPaintProperty('c-act', 'circle-radius', ['interpolate', ['linear'], ['get', 'act'], 1, 10, 3, 16]);
    setTimeout(function () {
      if (!map) { return; }
      map.setPaintProperty('c-act', 'circle-stroke-opacity-transition', { duration: 1400 });
      map.setPaintProperty('c-act', 'circle-stroke-opacity', 0.45);
    }, 1900);
  }

  var base = D.ml || '';
  if (!base || !D.tiles || !glEl || typeof Promise === 'undefined') { fail(); return; }
  Promise.all([import(base + 'maplibre-gl.js'), import(D.pm)]).then(boot, fail);
})();
